import { Injectable } from '@angular/core';

import { UserTypes } from '@site-mate/dashpivot-shared-library';

import { AppHierarchy } from 'app/shared/model/app-hierarchy.enum';
import { HttpClientService } from 'app/shared/service/http-client.service';

import { ICompareUsersParameters } from './compare-user-parameters.model';
import { SortOrder } from './sort-order.enum';
import { IUser } from '../user-list.model';

@Injectable({
  providedIn: 'root',
})
export class UserListService {
  folderHierarchy = {
    [AppHierarchy.Team]: 'teams',
    [AppHierarchy.Project]: 'projects',
    [AppHierarchy.Organisation]: 'companies',
  };

  constructor(private readonly http: HttpClientService) {}

  sortListByDirection(list: IUser[], column: string, direction: SortOrder) {
    const sortedList = [...list].sort((firstUser, secondUser) => {
      const result = this.compare({ firstUser, secondUser, column });
      return direction === SortOrder.ASC ? result : -result;
    });

    if (column === 'email') {
      return sortedList;
    }

    const sortedListWithPendingUsersOnTop = [...sortedList].sort((firstUser, secondUser) => {
      const result = this.comparePendingUsers({ firstUser, secondUser, direction, column });
      return direction === SortOrder.ASC ? result : -result;
    });

    return sortedListWithPendingUsersOnTop;
  }

  private compare({ firstUser, secondUser, column }: ICompareUsersParameters): number {
    if (firstUser[column] < secondUser[column]) {
      return -1;
    }
    if (firstUser[column] > secondUser[column]) {
      return 1;
    }
    return 0;
  }

  private comparePendingUsers({ firstUser, secondUser, direction }: ICompareUsersParameters): number {
    const isDescendingOrder = direction === SortOrder.DESC;
    const isAscendingOrder = direction === SortOrder.ASC;

    if (!firstUser.isActivated && secondUser.isActivated && isDescendingOrder) {
      return 1;
    }

    if (firstUser.isActivated && !secondUser.isActivated && isDescendingOrder) {
      return -1;
    }

    if (firstUser.isActivated && !secondUser.isActivated && isAscendingOrder) {
      return 1;
    }

    if (!firstUser.isActivated && secondUser.isActivated && isAscendingOrder) {
      return -1;
    }

    return 0;
  }

  filterList = (userList: IUser[], searchTerm: string): IUser[] => {
    if (!searchTerm) {
      return userList;
    }

    return userList.filter((user) => {
      const searchFields = [user.firstName, user.lastName, user.email, user.companyName];
      return searchFields.some((field) => field?.toLowerCase().includes(searchTerm.toLowerCase()));
    });
  };

  /**
   * Remove user from list of supression on postmarkApp based on hierarchy, entityId and email.
   * @example url:/teams/:teamId/email-suppressions/:email
   */

  removeEmailSuppression(email: string, hierarchy: AppHierarchy, entityId: string) {
    const encodedEmail = encodeURIComponent(email);

    const url = `${this.folderHierarchy[hierarchy]}/${entityId}/email-suppressions/${encodedEmail}`;

    return this.http.remove(url);
  }

  /**
   * Request postmarkApp to resend activation email to user based on hierarchy, entityId and email.
   * @example url:/teams/:teamId/activation-email/:email
   */
  resendActivationEmail(email: string, hierarchy: AppHierarchy, entityId: string, userType: UserTypes) {
    const encodedEmail = encodeURIComponent(email);

    const url = `users/${this.folderHierarchy[hierarchy]}/${entityId}/activation-email/${encodedEmail}`;

    return this.http.post(url, { userType, hierarchy });
  }

  /**
   * Return a unique user based on hierarchy, entityId and email.
   * @example url:/teams/:teamId/users/:email
   */

  getUserByEmail(email: string, hierarchy: AppHierarchy, entityId: string) {
    const encodedEmail = encodeURIComponent(email);

    const url = `${this.folderHierarchy[hierarchy]}/${entityId}/users?email=${encodedEmail}`;

    return this.http.get(url);
  }
}
